/**
 * components/store/StoreBar.tsx
 * Store selector and creator bar with per-user access:
 * - Admin: can see/manage all stores (add/edit/remove).
 * - Entry: sees only assigned stores (no manage buttons).
 * - If active store is not accessible, switch to the first accessible store automatically.
 */

import React, { useState } from 'react'
import { Plus, CheckCircle2, Pencil, Trash2 } from 'lucide-react'

import { useUserStore } from '@/store/user'
import { Await } from 'react-router'


/**
 * StoreCard
 * Small card to display a store with logo and name; click to activate.
 */
function StoreCard(props: {
  id: string
  name: string
  logoUrl?: string
  active?: boolean
  onClick: () => void
  onEdit?: () => void
  onRemove?: () => void
  canManage?: boolean
}) {
  const { id, name, logoUrl, active, onClick, onEdit, onRemove, canManage = false } = props
  const src =
    logoUrl ||
    'https://pub-cdn.sider.ai/u/U05XH97AEWZ/web-coder/68b268ce38697d89a13f32be/resource/cf5ab79e-20ec-49f2-a538-f1690f7499a1.jpg'

  return (
    <div
      key={id}
      className={`group relative flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition ${
        active ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'
      }`}
      onClick={onClick}
      title={active ? 'المتجر النشط' : 'تبديل إلى هذا المتجر'}
      aria-label={`${name} ${active ? '(نشط)' : ''}`}
    >
      <img src={src} alt={name} className="h-10 w-10 rounded-lg object-cover" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-slate-900">{name}</div>
        <div className="text-xs text-slate-500">{active ? 'نشط' : 'انقر للتبديل'}</div>
      </div>

      {canManage && (
        <div className="flex items-center gap-1">
          {active && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onEdit?.()
            }}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-100"
            title="تعديل"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              onRemove?.()
            }}
            className="rounded border border-rose-300 bg-white px-2 py-1 text-xs text-rose-700 hover:bg-rose-50"
            title="حذف المتجر"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  )
}

/**
 * AddOrEditStoreForm
 * Inline form to add a new store or edit existing meta (admin only visible).
 */
function AddOrEditStoreForm(props: {
  initial?: { id?: string; name?: string; logoUrl?: string }
  onCancel: () => void
}) {
  const { initial, onCancel } = props
  const addStore = useUserStore((s) => s.addStore)
  const setActiveStore = useUserStore((s) => s.setActiveStore)
  const updateStore = useUserStore((s) => s.updateStore)

  const [name, setName] = useState(initial?.name || '')
  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl || '')

  const isEdit = Boolean(initial?.id)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const cleanName = name.trim()
    const cleanLogo = logoUrl.trim()
    if (!cleanName) return

    if (isEdit && initial?.id) {
      updateStore(initial.id, cleanName, cleanLogo)
    } else {
      const id = await addStore(cleanName, cleanLogo)
      setActiveStore(id)
    }
    onCancel()
  }

  return (
    <form onSubmit={onSubmit} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <div className="sm:col-span-1">
        <label className="mb-1 block text-xs text-slate-600">اسم المتجر</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="مثال: متجر الفرع الرئيسي"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
        />
      </div>
      <div className="sm:col-span-1">
        <label className="mb-1 block text-xs text-slate-600">رابط الشعار (اختياري)</label>
        <input
          value={logoUrl}
          onChange={(e) => setLogoUrl(e.target.value)}
          placeholder="https://example.com/logo.png"
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-100"
        />
      </div>
      <div className="flex items-end gap-2">
        <button
          type="submit"
          className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
        >
          {isEdit ? 'حفظ' : 'إضافة المتجر'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg bg-gray-200 px-3 py-2 text-sm text-gray-800 hover:bg-gray-300"
        >
          إلغاء
        </button>
      </div>
    </form>
  )
}

/**
 * StoreBar
 * Shows current stores list with controls to create and select active store.
 * Respects user permissions (admin vs entry with assigned stores only).
 */
export  function StoreBar() {


  const stores = useUserStore((s) => s.stores)
  const activeStoreId = useUserStore((s) => s.activeStoreId)
  const setActiveStore = useUserStore((s) => s.setActiveStore)
  const role = useUserStore((s) => s.role)
  const removeStore = useUserStore((s) => s.removeStore)

  const isAdmin = role === 'admin'
  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [name, setName] = useState<string>('')
  const [logoUrl, setLogoUrl] = useState<string>('')

  const onRemove = async (id: string) => {
    const ok = confirm('هل تريد حذف هذا المتجر؟ هذا الإجراء لا يمكن التراجع عنه.')
    if (!ok) return
    await removeStore(id)
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" dir="rtl">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img
            src="https://pub-cdn.sider.ai/u/U05XH97AEWZ/web-coder/68b268ce38697d89a13f32be/resource/a1823f1d-e653-4e94-a715-8296de46b6ce.jpg"
            className="h-6 w-6 object-cover"
          />
          <h2 className="text-lg font-semibold">متاجري</h2>
        </div>
        {isAdmin && (
          <button
            onClick={() => {
              setEditId(null)
              setShowForm((v) => !v)
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-white hover:bg-slate-800"
          >
            <Plus className="h-4 w-4" /> إضافة متجر
          </button>
        )}
      </div>

      {isAdmin && showForm && (
        <div className="mb-3">
          <AddOrEditStoreForm
            initial={editId ? { id: editId, name: name, logoUrl: logoUrl } : undefined}
            onCancel={() => {
              setShowForm(false)
              setEditId(null)
            }}
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {stores.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-slate-500">
            {isAdmin ? 'لا توجد متاجر بعد. اضغط “إضافة متجر” للبدء.' : 'لا توجد متاجر مرتبطة بحسابك.'}
          </div>
        ) : (
          stores.map((m) => (
            <StoreCard
              key={m.id}
              id={m.id}
              name={m.name}
              logoUrl={m.logoUrl}
              active={m.id === activeStoreId}
              onClick={
                async () => 
                {
                 await setActiveStore(m.id)
                  setName(m.name)
                  setLogoUrl(m.logoUrl ? m.logoUrl : '')
                }} 
              onEdit={() => {
                setEditId(m.id)
                setShowForm(true)
              }}
              onRemove={() => onRemove(m.id)}
              canManage={isAdmin}
            />
          ))
        )}
      </div>
    </div>
  )
}
